"use client";

import { useCallback, useEffect, useState, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";

/**
 * The practice problem's sections, `EX-EDT01` (Wave 18, the problem page that
 * teaches): Statement first, then the tabs that open after a problem is tried.
 *
 * Buttons in a tablist rather than links, unlike the profile's tabs: the
 * editor on the right pane holds a draft and a verdict poll, and a navigation
 * would remount it. The tab is still addressable: it is written to `?tab=` with
 * `history.replaceState`, and read once on mount, so a shared link opens on
 * the section it was copied from while the canonical address stays the
 * problem's own.
 */
export const PROBLEM_TABS = ["statement", "editorial", "statistics", "submissions"] as const;
export type ProblemTab = (typeof PROBLEM_TABS)[number];

function isProblemTab(value: string | null): value is ProblemTab {
  return value !== null && (PROBLEM_TABS as readonly string[]).includes(value);
}

/**
 * The open tab, kept in `?tab=`. `available` is the set the page offers now;
 * a link naming a tab the problem does not have opens on the statement.
 */
export function useProblemTab(available: readonly ProblemTab[]) {
  const [tab, setTab] = useState<ProblemTab>("statement");

  useEffect(() => {
    try {
      const asked = new URLSearchParams(window.location.search).get("tab");
      if (isProblemTab(asked)) setTab(asked);
    } catch {
      // No URL to read (a test environment); the statement is the default.
    }
  }, []);

  const select = useCallback((next: ProblemTab) => {
    setTab(next);
    try {
      const url = new URL(window.location.href);
      if (next === "statement") url.searchParams.delete("tab");
      else url.searchParams.set("tab", next);
      window.history.replaceState(window.history.state, "", url.toString());
    } catch {
      // The tab still switches; only the address is left as it was.
    }
  }, []);

  return { tab: available.includes(tab) ? tab : "statement", select } as const;
}

export type ProblemTabItem = { key: ProblemTab; label: string };

export function ProblemTabs({
  items,
  active,
  onSelect,
  label,
  idPrefix,
}: {
  items: ProblemTabItem[];
  active: ProblemTab;
  onSelect: (tab: ProblemTab) => void;
  label: string;
  /** Prefix for the tab and panel ids, so `aria-controls` points somewhere real. */
  idPrefix: string;
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    const at = items.findIndex((item) => item.key === active);
    const step = event.key === "ArrowRight" ? 1 : -1;
    const next = items[(at + step + items.length) % items.length];
    if (!next) return;
    event.preventDefault();
    onSelect(next.key);
    document.getElementById(`${idPrefix}-tab-${next.key}`)?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="flex gap-1 overflow-x-auto border-b border-neutral-200"
    >
      {items.map((item) => {
        const current = item.key === active;
        return (
          <button
            key={item.key}
            id={`${idPrefix}-tab-${item.key}`}
            type="button"
            role="tab"
            aria-selected={current}
            aria-controls={`${idPrefix}-panel`}
            tabIndex={current ? 0 : -1}
            onClick={() => onSelect(item.key)}
            className={cn(
              "relative inline-flex min-h-11 shrink-0 items-center px-3 text-sm font-semibold transition-colors duration-200 ease-out",
              "after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:rounded-full after:transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              current ? "text-neutral-900 after:bg-primary" : "text-neutral-500 after:bg-transparent hover:text-neutral-900",
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
